import { useEffect, useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { voiceCall } from '@/data/voiceCalls';
import { t } from '@/i18n';
import { Bot, PhoneCall, PhoneOff, User } from 'lucide-react';
import { stateStyles, stateLabel } from './arrivalsUi';
import { HelpTip } from '@/components/ui/help-tip';

const STEP_MS = 1400;
const CALLABLE = ['no-info', 'rescheduled'];

// Simulated AI voice call to the dispatcher (#66): ringing, the transcript line by line,
// then the outcome is written to the loading. Marked as a simulation.
export const VoiceCall = ({ loading, carrier, onResult }) => {
  const [call, setCall] = useState(null); // { lines, outcome, shown, done }
  const timers = useRef([]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const start = () => {
    const script = voiceCall(loading, carrier);
    setCall({ ...script, shown: 0, done: false });
    script.lines.forEach((_, i) => timers.current.push(setTimeout(() => setCall(c => ({ ...c, shown: i + 1 })), STEP_MS * (i + 1))));
    timers.current.push(setTimeout(() => {
      setCall(c => ({ ...c, done: true }));
      if (script.outcome) onResult(loading.id, { ...script.outcome, channel: 'voice' });
    }, STEP_MS * (script.lines.length + 1)));
  };

  // A started call stays visible after it changes the loading's state.
  if (!call && !CALLABLE.includes(loading.state)) return null;
  if (!call) {
    return (
      <div className="flex items-center gap-1">
        <Button variant="outline" size="sm" className="gap-1 flex-1" onClick={start}>
          <PhoneCall className="h-3.5 w-3.5" /> {t('ai.voice.call')} {carrier.dispatcher.name}
        </Button>
        <HelpTip id="voice" />
      </div>
    );
  }
  return (
    <div className="rounded-xl border p-4 space-y-2" aria-live="polite">
      <div className="flex items-center gap-2 text-xs font-semibold">
        {call.done && !call.outcome ? <PhoneOff className="h-3.5 w-3.5 text-red-600" /> : <PhoneCall className={cn('h-3.5 w-3.5 text-primary', !call.done && 'animate-pulse')} />}
        {call.done ? t('ai.voice.ended') : call.shown === 0 ? t('ai.voice.ringing') : t('ai.voice.inProgress')}
        <Badge variant="outline" className="ml-auto text-[10px]">{t('ai.voice.simulation')}</Badge>
      </div>
      {call.lines.slice(0, call.shown).map((line, i) => (
        <div key={i} className={cn('flex gap-2 text-sm', line.who === 'human' && 'flex-row-reverse text-right')}>
          {line.who === 'bot' ? <Bot className="h-4 w-4 mt-0.5 shrink-0 text-primary" /> : <User className="h-4 w-4 mt-0.5 shrink-0" />}
          <div>
            <div>{line.text}</div>
            {line.textCs !== line.text && <div className="text-xs italic text-muted-foreground">{line.textCs}</div>}
          </div>
        </div>
      ))}
      {call.done && (
        call.outcome
          ? <div className="text-xs flex items-center gap-2 pt-1">{t('ai.voice.result')}: <Badge variant="outline" className={stateStyles[call.outcome.state]}>{stateLabel(call.outcome.state)}</Badge></div>
          : <div className="text-xs text-red-700 pt-1">{t('ai.voice.noAnswer')}</div>
      )}
    </div>
  );
};
